// client/src/components/BugReporter.jsx
//
// "Report a bug" button + modal, matched to swa-rprs's BugReporter: a solid,
// centered, dark-teal-header modal with a "Report a bug" / "My reports" tab
// strip, a captured technical-details disclosure (from utils/bugLog), and the
// KbBees success cheer. Rendered through a portal to document.body so it always
// centers over the viewport regardless of page layout.
//
// Self-contained (no shared store / board here — that lives in admin-hq): submit
// POSTs to /api/bug-report → func-shared-services (sole writer to
// Feedback.BugReports, ContextApp='data-integrity'); "My reports" reads
// /api/bug-reports {scope:'mine'}. Reports surface on admin-hq's cross-app Dev
// Team board. Sits alongside the existing "Help" email button in TopBar.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { snapshotLogs, snapshotEvents, snapshotClientContext } from '../utils/bugLog';
import KbBees, { preloadKbBees } from './KbBees';

const BUG_EMOJI = '\u{1F41B}'; // 🐛 — the exact glyph the suite uses

const COLORS = {
  brandDark: '#003D4C', brandGreen: '#6CC24A', bg: '#ffffff',
  text: '#1f2937', muted: '#6b7280', border: '#e5e7eb', cardBg: '#f8fafc',
};
const textareaStyle = {
  width: '100%', boxSizing: 'border-box', resize: 'vertical',
  border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px',
  fontSize: 13.5, fontFamily: 'inherit', lineHeight: 1.45, color: '#1f2937',
};
const primaryBtn = {
  padding: '10px 22px', background: COLORS.brandGreen, color: 'white',
  border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const secondaryBtn = {
  padding: '10px 18px', background: 'white', color: COLORS.muted,
  border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

// func statuses: open | triaged | in_progress | resolved | wont_fix
const STATUS_LABELS = {
  open: { t: 'Open', c: '#92400E', b: '#FEF3C7' },
  triaged: { t: 'Triaged', c: '#92400E', b: '#FEF3C7' },
  in_progress: { t: 'In progress', c: '#3730A3', b: '#E0E7FF' },
  resolved: { t: 'Resolved', c: '#065F46', b: '#D1FAE5' },
  wont_fix: { t: "Won't fix", c: '#6b7280', b: '#F3F4F6' },
};

function relativeTime(iso) {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 45) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

function Field({ label, required, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
        {label}{required && <span style={{ color: '#dc2626' }}> *</span>}
      </label>
      {children}
    </div>
  );
}

function TabBtn({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        flex: 1, padding: '10px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontSize: 13, fontWeight: 700, color: active ? COLORS.brandDark : COLORS.muted,
        borderBottom: active ? `2px solid ${COLORS.brandGreen}` : '2px solid transparent',
      }}>
      {children}
    </button>
  );
}

function MyReportsPanel({ loading, reports, onReport }) {
  if (loading) {
    return <div style={{ padding: '28px 24px', textAlign: 'center', color: COLORS.muted, fontSize: 13 }}>Loading your reports…</div>;
  }
  return (
    <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14, minHeight: 160 }}>
      {reports.length === 0 ? (
        <div style={{ textAlign: 'center', color: COLORS.muted, padding: '28px 12px' }}>
          <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.4 }}>{BUG_EMOJI}</div>
          <div style={{ fontSize: 13, fontWeight: 600 }}>You haven't reported any bugs yet.</div>
          <button onClick={onReport} style={{ ...secondaryBtn, marginTop: 14 }}>Report a bug</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => {
            const s = STATUS_LABELS[r.status] || STATUS_LABELS.open;
            return (
              <div key={r.bugReportId} style={{ border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '10px 12px', background: COLORS.cardBg }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{ fontWeight: 600, color: COLORS.text, fontSize: 13.5, lineHeight: 1.3 }}>{r.issue}</div>
                  <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, color: s.c, background: s.b, whiteSpace: 'nowrap' }}>{s.t}</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.muted, marginTop: 3 }}>#{r.bugReportId} · {r.when}</div>
                {r.resolutionNote && <div style={{ fontSize: 12, color: COLORS.text, marginTop: 6 }}><strong>Update:</strong> {r.resolutionNote}</div>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function BugReporter() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('report');
  const [issue, setIssue] = useState('');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [showCaptured, setShowCaptured] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);
  const [done, setDone] = useState(null); // reference # of the sent report
  const [myReports, setMyReports] = useState([]);
  const [myLoading, setMyLoading] = useState(false);

  useEffect(() => { if (open) preloadKbBees(); }, [open]);

  // Load "My reports" when that tab opens.
  useEffect(() => {
    if (!open || tab !== 'mine') return;
    let cancelled = false;
    setMyLoading(true);
    fetch('/api/bug-reports', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ scope: 'mine' }),
    })
      .then(r => r.json().catch(() => ({})))
      .then(data => {
        if (cancelled) return;
        const rows = (data && data.reports ? data.reports : []).map(row => ({
          bugReportId: Number(row.bugReportId),
          issue: row.title || row.doing || '(untitled)',
          status: row.status || 'open',
          when: relativeTime(row.createdAt),
          resolutionNote: row.resolutionNote || null,
        }));
        setMyReports(rows);
      })
      .catch(() => { if (!cancelled) setMyReports([]); })
      .finally(() => { if (!cancelled) setMyLoading(false); });
    return () => { cancelled = true; };
  }, [open, tab]);

  const reset = () => { setIssue(''); setDescription(''); setSteps(''); setShowCaptured(false); setDone(null); setSubmitErr(null); };
  const close = () => { setOpen(false); reset(); setTab('report'); };
  const openModal = () => { setOpen(true); setTab('report'); };

  const logCount = open ? snapshotLogs().length : 0;
  const eventCount = open ? snapshotEvents().length : 0;

  const canSubmit = issue.trim().length > 0 && !submitting;
  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const res = await fetch('/api/bug-report', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({
          title: issue.trim().slice(0, 200),
          description: { doing: issue.trim(), happened: description.trim(), repro: steps.trim() },
          context: snapshotClientContext(),
          logs: snapshotLogs(),
          events: snapshotEvents(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || (data && data.error)) throw new Error((data && data.error) || `HTTP ${res.status}`);
      setDone(Number(data.bugReportId) || 0);
    } catch (e) {
      setSubmitErr((e && e.message) || 'Could not send the report.');
    } finally {
      setSubmitting(false);
    }
  };

  const trigger = useMemo(() => (
    <button type="button" className="topbar-help-btn" onClick={openModal} title="Report a bug to the dev team">
      <span aria-hidden="true">{BUG_EMOJI}</span> Report a bug
    </button>
  ), []);

  return (
    <>
      {trigger}

      {open && createPortal(
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div style={{
            background: COLORS.bg, color: COLORS.text, borderRadius: 14,
            width: 'min(580px, 94vw)', maxHeight: '90vh', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', boxShadow: '0 16px 48px rgba(0,0,0,.35)',
          }}>
            {/* Header */}
            <div style={{ background: COLORS.brandDark, color: 'white', padding: '18px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 26, lineHeight: 1 }}>{BUG_EMOJI}</span>
              <div>
                <div style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.1 }}>Report a bug</div>
                <div style={{ fontSize: 13, color: '#cbe5d8', marginTop: 2 }}>
                  Tell us what went wrong — we'll attach the technical details automatically.
                </div>
              </div>
            </div>

            {/* Tab strip */}
            <div style={{ display: 'flex', borderBottom: `1px solid ${COLORS.border}`, background: '#fff' }}>
              <TabBtn active={tab === 'report'} onClick={() => setTab('report')}>Report a bug</TabBtn>
              <TabBtn active={tab === 'mine'} onClick={() => setTab('mine')}>My reports</TabBtn>
            </div>

            {tab === 'mine' ? (
              <MyReportsPanel loading={myLoading} reports={myReports} onReport={() => setTab('report')} />
            ) : done !== null ? (
              // Success state
              <div style={{ padding: '28px 24px', textAlign: 'center' }}>
                <KbBees size={132} fps={8} />
                <div style={{ fontSize: 17, fontWeight: 700, color: COLORS.brandDark }}>Thanks — bug reported.</div>
                <p style={{ fontSize: 13.5, color: COLORS.muted, margin: '8px 0 0', lineHeight: 1.5 }}>
                  {done ? <>Reference <strong>#{done}</strong>. </> : null}
                  It's on the dev team's board, with the page you were on and recent app activity attached.
                </p>
                <div style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <button onClick={close} style={primaryBtn}>Close</button>
                  <button onClick={reset} style={secondaryBtn}>Report another</button>
                </div>
              </div>
            ) : (
              <>
                {/* Body */}
                <div style={{ padding: '18px 24px', overflowY: 'auto', fontSize: 14 }}>
                  <Field label="Issue" required>
                    <textarea value={issue} onChange={e => setIssue(e.target.value)} placeholder="A short summary of the problem" rows={2} style={textareaStyle} autoFocus />
                  </Field>
                  <Field label="Description">
                    <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="What happened, and any error you saw" rows={3} style={textareaStyle} />
                  </Field>
                  <Field label="Steps to Reproduce">
                    <textarea value={steps} onChange={e => setSteps(e.target.value)} placeholder={'1. …\n2. …\n3. …'} rows={3} style={textareaStyle} />
                  </Field>

                  {/* Captured-context disclosure */}
                  <button
                    type="button"
                    onClick={() => setShowCaptured(s => !s)}
                    style={{ background: 'none', border: 'none', padding: 0, marginTop: 4, color: COLORS.brandDark, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {showCaptured ? '▾' : '▸'} We'll also include page &amp; technical details ({logCount} log lines, {eventCount} events)
                  </button>
                  {showCaptured && (
                    <ul style={{ margin: '8px 0 0', padding: '10px 10px 10px 26px', fontSize: 12.5, color: COLORS.muted, background: COLORS.cardBg, borderRadius: 8, lineHeight: 1.6 }}>
                      <li>Current page URL &amp; route</li>
                      <li>Your sign-in (email)</li>
                      <li>Browser, screen &amp; viewport size</li>
                      <li>Recent console logs and any errors captured this session</li>
                    </ul>
                  )}

                  {submitErr && (
                    <div style={{ marginTop: 12, padding: '8px 10px', borderRadius: 8, background: '#FEF2F2', color: '#991B1B', fontSize: 12.5, border: '1px solid #FECACA' }}>
                      {submitErr}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{ padding: '14px 24px 18px', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={close} style={secondaryBtn}>Cancel</button>
                  <button onClick={submit} disabled={!canSubmit}
                    style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.55, cursor: canSubmit ? 'pointer' : 'not-allowed' }}>
                    {submitting ? 'Sending…' : 'Send report'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
